/**
 * Analytics — type-safe dataLayer event helpers for GTM.
 *
 * GTM picks up these events and routes them to GA4, Google Ads,
 * Meta Pixel, etc. as configured in the GTM console.
 *
 * Usage: import { trackSignUp } from "@/lib/analytics" then call
 * after the action succeeds.
 */

// Extend the Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/** Push a custom event to the GTM dataLayer. */
function pushEvent(event: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

// ── Funnel Events ──────────────────────────────────────────────

/** Fired after successful account creation (email, Google, or Apple). */
export function trackSignUp(method: "email" | "google" | "apple"): void {
  pushEvent("sign_up", { method });
}

/** Fired after successful sign-in (email or Google). */
export function trackLogin(method: "email" | "google"): void {
  pushEvent("login", { method });
}

/** Fired after successful Stripe checkout (redirect back with ?success=true). */
export function trackUpgradeToPro(interval: "monthly" | "annual"): void {
  pushEvent("upgrade_to_pro", { billing_interval: interval });
}

// ── Ad-platform conversion events ──────────────────────────────
//
// These mirror Meta Pixel's standard event names so a single GTM tag
// can route the dataLayer event straight to Pixel's CompleteRegistration
// and Subscribe events with no extra translation. Same payload feeds
// Google Ads via a GTM conversion-tracking tag (config side, not code).
//
// `value` reflects what Meta sees as immediate revenue. The annual flow
// charges $0 for the 30-day Pro preview, so we send value=0 + a
// predicted_ltv so Meta's optimizer still bids meaningfully. Monthly
// charges immediately and sends the actual price.
//
// Server-side dedup: the RC → Meta Conversions API + RC → Google Ads
// integrations should be enabled in the RC dashboard so iOS/Android
// purchases get reported with the same event_id, and so we have a
// server-side signal even when Apple's privacy framework blocks the
// client-side Pixel hit.

/** Fired once after account creation (any method). Mirrors Pixel's
 *  CompleteRegistration. Value is 0 because the free tier has no
 *  immediate revenue. */
export function trackCompleteRegistration(method: "email" | "google" | "apple"): void {
  pushEvent("complete_registration", {
    content_name: "RevHead Pro Account",
    method,
    value: 0,
    currency: "USD",
  });
}

/** Fired after a successful Pro checkout (web → Stripe success page).
 *  Pairs with trackUpgradeToPro for funnel reporting; this is the
 *  ad-platform conversion mirror. */
export function trackSubscribe(params: {
  interval: "monthly" | "annual";
  monthlyPrice: number;
  annualPrice: number;
}): void {
  const { interval, monthlyPrice, annualPrice } = params;
  // Annual: $0 today (30-day preview), predicted LTV is the annual price.
  // Monthly: charged immediately, predicted LTV is roughly the annual run-rate.
  const value = interval === "annual" ? 0 : monthlyPrice;
  const predictedLtv = interval === "annual" ? annualPrice : monthlyPrice * 12;
  pushEvent("subscribe", {
    billing_interval: interval,
    value,
    currency: "USD",
    predicted_ltv: predictedLtv,
  });
}

/** Fired once per session when a user lands on a page with a promo code in the URL. */
export function trackPromoCodeSeen(params: {
  code: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
}): void {
  pushEvent("promo_code_seen", {
    promo_code: params.code,
    utm_source: params.source,
    utm_medium: params.medium,
    utm_campaign: params.campaign,
  });
}

/** Fired after successful checkout when a promo code was applied. */
export function trackPromoRedeemed(params: { code: string; platform: "web" }): void {
  pushEvent("promo_redeemed", {
    promo_code: params.code,
    platform: params.platform,
  });
}

// ── Activation Events ──────────────────────────────────────────

/** Fired after a vehicle is successfully created. */
export function trackCreateVehicle(vehicleType: string): void {
  pushEvent("create_vehicle", { vehicle_type: vehicleType });
}

/** Fired after a support request is submitted. */
export function trackSubmitSupport(): void {
  pushEvent("submit_support");
}

// ── Add-vehicle wizard funnel ──────────────────────────────────

/** User landed on step 1 of the add-vehicle wizard. */
export function trackAddVehicleStarted(garageId?: string): void {
  pushEvent("add_vehicle_started", { garage_id: garageId });
}

/** User clicked "Next" on step 1 (form was valid). */
export function trackAddVehicleStep1Complete(vehicleType: string, make: string): void {
  pushEvent("add_vehicle_step_1_complete", {
    vehicle_type: vehicleType,
    make,
  });
}

/** User went back from step 2 to step 1 — drop-off signal. */
export function trackAddVehicleStep2Back(): void {
  pushEvent("add_vehicle_step_2_back");
}

/** Vehicle was created with isPublic:true. `hasHandle` is true if a handle was claimed in the same flow. */
export function trackAddVehicleMadePublic(hasHandle: boolean): void {
  pushEvent("add_vehicle_made_public", { has_handle: hasHandle });
}

/** Ride handle was successfully claimed during the wizard. Handle string itself is intentionally not sent. */
export function trackAddVehicleHandleClaimed(): void {
  pushEvent("add_vehicle_handle_claimed");
}

/** Vehicle was created on the public path with no handle (user typed nothing or tapped skip-for-now). */
export function trackAddVehicleHandleSkipped(): void {
  pushEvent("add_vehicle_handle_skipped");
}

/** UX-friction signal: availability check returned `false` for the user's chosen handle. */
export function trackAddVehicleHandleTakenSeen(): void {
  pushEvent("add_vehicle_handle_taken_seen");
}

/** User chose a photo on the photo+nickname step (before commit). */
export function trackAddVehiclePhotoChosen(): void {
  pushEvent("add_vehicle_photo_chosen");
}

/** User skipped the photo step entirely. */
export function trackAddVehiclePhotoSkipped(): void {
  pushEvent("add_vehicle_photo_skipped");
}

// ── Free tier expansion (plan 24 phase 3) ──────────────────────

/** Banner first rendered to an eligible user. */
export function trackFreeTierExpansionBannerShown(): void {
  pushEvent("free_tier_expansion_banner_shown");
}

/** User tapped the "See what's new" CTA in the banner. */
export function trackFreeTierExpansionWalkthroughOpened(): void {
  pushEvent("free_tier_expansion_walkthrough_opened");
}

/** User completed the 4-card walkthrough by tapping "Got it". */
export function trackFreeTierExpansionWalkthroughCompleted(): void {
  pushEvent("free_tier_expansion_walkthrough_completed");
}

/** User dismissed without seeing the walkthrough (X button on banner). */
export function trackFreeTierExpansionDismissedEarly(): void {
  pushEvent("free_tier_expansion_dismissed_early");
}
